import { storyblokEditable } from "@storyblok/react";

const Chart = ({ blok }) => {
  const columnHeadings =
    blok.column_headings
      ?.split("\n")
      .map((h) => h.trim())
      .filter((h) => h.length > 0) || [];

  const addRightSpacer = !!blok.add_right_spacer;
  const productColumnCount = columnHeadings.length;
  const totalProductColumns = productColumnCount + (addRightSpacer ? 1 : 0);
  const rows = blok.rows || [];

  return (
    <section
      {...storyblokEditable(blok)}
      className="w-full font-['Roboto'] my-12 px-4 md:px-8"
    >
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        {blok.title && (
          <h2
            className="mb-6 text-left"
            style={{
              fontSize: "30px",
              fontWeight: 500,
            }}
          >
            {blok.title}
          </h2>
        )}

        <div className="overflow-x-auto">
          {/* Thin border */}
          <div className="inline-block min-w-full border border-gray-300 rounded-md overflow-hidden">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-[#00033E] text-white">
                  {/* labels should stay aligned */}
                  <th
                    className="
                      sticky left-0 z-20
                      px-3 py-3 text-xs
                      md:px-6 md:py-4 md:text-[16px]
                      font-bold text-left
                      bg-[#00033E]
                    "
                  />

                  {columnHeadings.map((heading, index) => (
                    <th
                      key={index}
                      className="
                        px-3 py-3 text-xs
                        md:px-6 md:py-4 md:text-[16px]
                        font-bold text-left align-bottom
                      "
                    >
                      {heading}
                    </th>
                  ))}

                  {addRightSpacer && (
                    <th
                      className="
                        px-3 py-3 text-xs
                        md:px-6 md:py-4 md:text-[16px]
                        font-bold text-left
                      "
                    />
                  )}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={1 + totalProductColumns}
                      className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm text-red-600"
                    >
                      No rows found on the “rows” field of this Chart blok.
                      Make sure the field name is <strong>rows</strong> and it
                      contains <strong>ChartRow</strong> items.
                    </td>
                  </tr>
                )}

                {rows.map((row, index) => (
                  <ChartRow
                    key={row._uid || index}
                    row={row}
                    rowIndex={index}
                    columnCount={totalProductColumns}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

// Row
const ChartRow = ({ row, rowIndex, columnCount }) => {
  const isStriped = rowIndex % 2 === 1;
  const cells = row.cells || [];
  const missingCells = Math.max(0, columnCount - cells.length);

  const rowBg = isStriped ? "#E3E3E3" : "#FFFFFF";

  return (
    <tr
      {...storyblokEditable(row)}
      style={{ backgroundColor: rowBg }}
    >
      {/* Left label */}
      <th
        scope="row"
        className="
          sticky left-0 z-10
          px-3 py-3 text-xs
          md:px-6 md:py-4 md:text-[16px]
          text-left align-top font-normal
        "
        style={{ backgroundColor: rowBg }}
      >
        {row.label}
      </th>

      {cells.map((cell, idx) => (
        <ChartCell key={cell._uid || idx} cell={cell} />
      ))}

      {Array.from({ length: missingCells }).map((_, idx) => (
        <td
          key={`empty-${idx}`}
          className="
            px-3 py-3 text-xs
            md:px-6 md:py-4 md:text-[16px]
          "
        />
      ))}
    </tr>
  );
};

// Cell
const ChartCell = ({ cell }) => {
  const type = cell.content_type || "text";

  let content = null;

  if (type === "check") {
    // Checkmark icon that appears when check is selected
    content = (
      <span
        aria-label="Included"
        role="img"
        className="inline-flex items-center justify-center"
      >
        <svg
          viewBox="0 0 16 16"
          className="w-4 h-4 md:w-5 md:h-5"
        >
          <polyline
            points="3 9 7 13 13 3"
            fill="none"
            stroke="#00A651"
            strokeWidth="2.5"
            strokeLinecap="square"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  } else if (type === "na") {
    content = <span>N/A</span>;
  } else if (type === "text") {
    content = cell.text || "";
  } else {
    content = "";
  }

  return (
    <td
      {...storyblokEditable(cell)}
      className="
        px-3 py-3 text-xs
        md:px-6 md:py-4 md:text-[16px]
        align-top font-normal text-gray-900
      "
    >
      {content}
    </td>
  );
};

export default Chart;
